// Header.js
import React, { useState, useEffect, useRef, useMemo, memo } from 'react';
import { RxDownload, RxMagnifyingGlass } from "react-icons/rx";
import { Bars3Icon } from "@heroicons/react/24/outline";
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon, ArrowDownTrayIcon, SunIcon, MoonIcon, ChevronUpIcon, ChevronDownIcon, XMarkIcon, ArrowLeftIcon, BellIcon } from "@heroicons/react/24/outline";
import { HiDotsVertical } from "react-icons/hi";
import { HeaderModelSelector } from './HeaderModelSelector';

const COMPANY_DISPLAY_NAME_KEY = 'cachedCompanyDisplayName';

const readCachedCompanyName = () => {
  if (typeof window === 'undefined') return null;
  try {
    const cached = localStorage.getItem(COMPANY_DISPLAY_NAME_KEY);
    if (cached) return cached;
    const storedUser = localStorage.getItem('user');
    if (storedUser && storedUser !== 'undefined') {
      const parsed = JSON.parse(storedUser);
      return parsed?.company?.nickname || parsed?.company?.name || null;
    }
  } catch (error) {
    console.warn('Unable to read cached company name', error);
  }
  return null;
};

export function Header({ user, currentChatId, messages, onSearch, onMenuClick, className = '', isSidebarCollapsed = false, isProjectChat = false, projectId = null, chatTitle = null, activeUsers = [], projectName = null, isProjectDashboard = false, onProjectSettings = null, isClientChat = false, clientId = null, clientName = null, selectedModel = null, onModelSelect = null, showModelSelector = false, onNotificationsClick = null, notificationCount = 0, isNotificationsOpen = false, onCloseNotifications = null }) {
  const [hasBotResponses, setHasBotResponses] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [currentResultIndex, setCurrentResultIndex] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const searchInputRef = useRef(null);
  const location = useLocation();
  const [cachedCompanyName, setCachedCompanyName] = useState(readCachedCompanyName);
  const navigate = useNavigate();
  const isSettingsOrProfile = location.pathname.includes('/settings') || location.pathname.includes('/profile');

  useEffect(() => {
    const nextName = user?.company?.nickname || user?.company?.name;
    if (!nextName || nextName === cachedCompanyName) {
      return;
    }

    setCachedCompanyName(nextName);
    try {
      localStorage.setItem(COMPANY_DISPLAY_NAME_KEY, nextName);
    } catch (error) {
      console.warn('Unable to cache company name', error);
    }
  }, [user?.company?.nickname, user?.company?.name, cachedCompanyName]);
  
  // Profile stack hover menu state moved to HeaderProfileStack component
  
  // Add state for actions menu
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
  const actionsMenuTimeoutRef = useRef(null);
  const actionsMenuRef = useRef(null);
  const actionsMenuCooldownRef = useRef(false);

  // Reset menu and suppress hover when notifications close (cursor may be over 3 dots)
  useEffect(() => {
    if (!isNotificationsOpen) {
      setIsActionsMenuOpen(false);
      if (actionsMenuTimeoutRef.current) {
        clearTimeout(actionsMenuTimeoutRef.current);
        actionsMenuTimeoutRef.current = null;
      }
      actionsMenuCooldownRef.current = true;
      const t = setTimeout(() => { actionsMenuCooldownRef.current = false; }, 300);
      return () => clearTimeout(t);
    }
  }, [isNotificationsOpen]);
  
  // Check if there are any bot responses in the current chat
  useEffect(() => {
    if (messages && messages.length > 0) {
      const botMessages = messages.filter(msg => msg && msg.sender === 'bot');
      setHasBotResponses(botMessages.length > 0);
    } else {
      setHasBotResponses(false);
    }
  }, [messages]);


  
  // Handle PDF download
  const handleDownload = async () => {
    if (!currentChatId) {
      console.log('No currentChatId available');
      return;
    }

    setIsDownloading(true);
    setDownloadProgress(0);
    console.log('Starting download for chat:', currentChatId);
    try {
      // Get the chat title from the API
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('No auth token found');
        return;
      }

      console.log('Fetching chat preview...');
      const chatResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/chat/${currentChatId}/preview`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('Chat preview response:', chatResponse.data);
      const chatTitle = chatResponse.data.chat?.title || 'Chat';
      const cleanTitle = chatTitle.replace(/[^\w\s-]/g, '').replace(/\s+/g, '_');

      // Format the current date as MM-DD-YY
      const today = new Date();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const year = String(today.getFullYear()).slice(-2);
      const formattedDate = `${month}-${day}-${year}`;

      // Create the filename in the specified format
      const filename = `Midus_${cleanTitle}_${formattedDate}.pdf`;
      console.log('Generated filename:', filename);

      console.log('Downloading PDF...');
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/chat/${currentChatId}/download`,
        {
          params: { timezone: userTimezone },
          headers: { 
            Authorization: `Bearer ${token}`,
          },
          responseType: 'blob',
          onDownloadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setDownloadProgress(percentCompleted);
          }
        }
      );

      console.log('PDF download response received');
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      console.log('Download initiated');
    } catch (error) {
      console.error('Error downloading chat:', error);
      if (error.response) {
        console.error('Error response:', error.response.status, error.response.data);
      }
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
    }
  };

  const handlePreview = () => {
    window.open(`/pdf-preview/${currentChatId}`, '_blank');
  };

  // Helper function to escape regex special characters
  const escapeRegExp = (str) => {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  // Search function to find all matches in the chat
  const performSearch = (query) => {
    if (!query.trim() || !messages?.length) {
      setSearchResults([]);
      setCurrentResultIndex(0);
      // Call onSearch with null to clear search
      if (onSearch) {
        console.log('Clearing search state');
        onSearch(null);
      }
      
      // Dispatch clear event
      window.dispatchEvent(new CustomEvent('search-clear'));
      return;
    }
    
    console.log('Performing search for:', query);
    
    const results = [];
    const escapedQuery = escapeRegExp(query);
    const regex = new RegExp(`(${escapedQuery})`, 'gi');
    
    // Search through all messages
    messages.forEach((message, messageIndex) => {
      if (!message?.text) return;
      
      const text = message.text;
      
      // Find all matches with regex
      const matches = [];
      let match;
      
      // Reset regex for each message
      regex.lastIndex = 0;
      
      while ((match = regex.exec(text)) !== null) {
        matches.push({
          messageId: message._id || `msg-${messageIndex}`,
          messageIndex,
          position: match.index,
          matchedText: match[0],
          length: match[0].length
        });
      }
      
      // Add all matches from this message to results
      if (matches.length > 0) {
        console.log(`Message ${messageIndex} has ${matches.length} matches for "${query}"`);
        results.push(...matches);
      }
    });
    
    setSearchResults(results);
    setCurrentResultIndex(results.length > 0 ? 0 : -1);
    
    // Call onSearch prop to update search state in Chat component
    if (results.length > 0) {
      const searchData = {
        query: query,
        activeMessageIndex: results[0].messageIndex,
        activeMatchIndex: 0,
        totalMatches: results.length
      };
      console.log('Sending search data to Chat component:', JSON.stringify(searchData));
      
      if (onSearch) {
        onSearch(searchData);
      }
      
      // Dispatch event for components to highlight and scroll
      window.dispatchEvent(new CustomEvent('search-result', { 
        detail: {
          query: query, // Send original query to preserve case
          currentResult: results[0],
          allResults: results
        }
      }));
    } else {
      console.log('No search results found for:', query);
      // Dispatch clear event
      window.dispatchEvent(new CustomEvent('search-clear'));
      // Update UI to show no results
      if (onSearch) {
        onSearch(null);
      }
    }
  };

  const handleSearchInput = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    performSearch(query);
  };

  const navigateToNextResult = () => {
    if (!searchResults.length) return;
    
    const nextIndex = (currentResultIndex + 1) % searchResults.length;
    setCurrentResultIndex(nextIndex);
    
    // Get the current result
    const result = searchResults[nextIndex];
    
    // Update search state in Chat component
    if (onSearch) {
      onSearch({
        query: searchQuery,
        activeMessageIndex: result.messageIndex,
        activeMatchIndex: nextIndex,
        totalMatches: searchResults.length
      });
    }
    
    // Dispatch event to scroll to this result
    window.dispatchEvent(new CustomEvent('search-result', { 
      detail: {
        query: searchQuery, // Use original query to preserve case
        currentResult: result,
        allResults: searchResults
      }
    }));
    
    console.log(`Navigated to result ${nextIndex + 1}/${searchResults.length} in message index ${result.messageIndex}`);
  };

  const navigateToPrevResult = () => {
    if (!searchResults.length) return;
    
    const prevIndex = currentResultIndex <= 0 ? searchResults.length - 1 : currentResultIndex - 1;
    setCurrentResultIndex(prevIndex);
    
    // Get the current result
    const result = searchResults[prevIndex];
    
    // Update search state in Chat component
    if (onSearch) {
      onSearch({
        query: searchQuery,
        activeMessageIndex: result.messageIndex,
        activeMatchIndex: prevIndex,
        totalMatches: searchResults.length
      });
    }
    
    // Dispatch event to scroll to this result
    window.dispatchEvent(new CustomEvent('search-result', { 
      detail: {
        query: searchQuery, // Use original query to preserve case
        currentResult: result,
        allResults: searchResults
      }
    }));
    
    console.log(`Navigated to result ${prevIndex + 1}/${searchResults.length} in message index ${result.messageIndex}`);
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setCurrentResultIndex(-1);
    setIsSearchOpen(false);
    
    // Notify Chat component to clear search
    if (onSearch) {
      onSearch(null);
    }
    
    // Dispatch event to clear highlights
    window.dispatchEvent(new CustomEvent('search-clear'));
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + F to open search
      if ((e.ctrlKey || e.metaKey) && e.key === 'f' && hasBotResponses) {
        e.preventDefault();
        setIsSearchOpen(true);
        setTimeout(() => {
          if (searchInputRef.current) {
            searchInputRef.current.focus();
          }
        }, 10);
      }
      
      // Escape to close search
      if (e.key === 'Escape' && isSearchOpen) {
        e.preventDefault();
        clearSearch();
      }
      
      if (isSearchOpen && searchResults.length > 0) {
        // Enter to go to next result
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          navigateToNextResult();
        }
        
        // Shift + Enter to go to previous result
        if (e.key === 'Enter' && e.shiftKey) {
          e.preventDefault();
          navigateToPrevResult();
        }
        
        // F3 to go to next result
        if (e.key === 'F3' && !e.shiftKey) {
          e.preventDefault();
          navigateToNextResult();
        }
        
        // Shift + F3 to go to previous result
        if (e.key === 'F3' && e.shiftKey) {
          e.preventDefault();
          navigateToPrevResult();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSearchOpen, hasBotResponses, searchResults, currentResultIndex]);

  // Focus search input when search is opened
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  // Listen for clear header icons event from ProjectDashboard
  useEffect(() => {
    const handleClearHeaderIcons = () => {
      // Clear search state
      setIsSearchOpen(false);
      setSearchQuery('');
      setSearchResults([]);
      setCurrentResultIndex(-1);
      
      // Clear bot responses state to hide the icons
      setHasBotResponses(false);
      
      // Notify Chat component to clear search if onSearch is available
      if (onSearch) {
        onSearch(null);
      }
      
      // Dispatch event to clear highlights
      window.dispatchEvent(new CustomEvent('search-clear'));
    };

    window.addEventListener('clearHeaderIcons', handleClearHeaderIcons);
    
    return () => {
      window.removeEventListener('clearHeaderIcons', handleClearHeaderIcons);
    };
  }, [onSearch]);

  // Handle return to project dashboard
  const handleReturnToProject = () => {
    if (projectId) {
      navigate(`/project/${projectId}`);
    } else {
      // Fallback to projects list if no specific project ID
      navigate('/projects');
    }
  };

  // Profile menu handlers


  // Actions menu handlers
  const handleActionsMenuEnter = () => {
    if (actionsMenuCooldownRef.current) return;
    if (actionsMenuTimeoutRef.current) {
      clearTimeout(actionsMenuTimeoutRef.current);
      actionsMenuTimeoutRef.current = null;
    }
    setIsActionsMenuOpen(true);
  };

  const handleActionsMenuLeave = () => {
    // Add a small delay before closing to allow mouse to move to menu
    actionsMenuTimeoutRef.current = setTimeout(() => {
      setIsActionsMenuOpen(false);
    }, 150);
  };

  // Handle actions from the menu
  const handleFindInChat = () => {
    setIsActionsMenuOpen(false);
    setIsSearchOpen(true);
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 10);
  };

  const handleDownloadFromMenu = () => {
    setIsActionsMenuOpen(false);
    handleDownload();
  };

  // Updated color palette with new hex values (same as ProjectDashboard)
  const pastelColors = [
    { bg: 'bg-[#F5C8BD]', hex: '#F5C8BD' },
    { bg: 'bg-[#E9BACC]', hex: '#E9BACC' },
    { bg: 'bg-[#D0AAB0]', hex: '#D0AAB0' },
    { bg: 'bg-[#B599C1]', hex: '#B599C1' },
    { bg: 'bg-[#ABACCB]', hex: '#ABACCB' },
    { bg: 'bg-[#9FBFDD]', hex: '#9FBFDD' },
    { bg: 'bg-[#97CCDC]', hex: '#97CCDC' },
    { bg: 'bg-[#83C4B4]', hex: '#83C4B4' },
    { bg: 'bg-[#A9D194]', hex: '#A9D194' },
    { bg: 'bg-[#D5E4AD]', hex: '#D5E4AD' },
    { bg: 'bg-[#FAEEAE]', hex: '#FAEEAE' },
    { bg: 'bg-[#F5BE6B]', hex: '#F5BE6B' }
  ];

  // Function to generate a darker text color based on the background color
  const getDarkerTextColor = (hexColor) => {
    // Remove the # if present
    const hex = hexColor.replace('#', '');
    
    // Parse RGB values
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Generate darker versions (reduce each component by about 40-60%)
    const darkerR = Math.max(0, Math.floor(r * 0.4));
    const darkerG = Math.max(0, Math.floor(g * 0.4));
    const darkerB = Math.max(0, Math.floor(b * 0.4));
    
    // Convert back to hex
    const toHex = (n) => n.toString(16).padStart(2, '0');
    return `#${toHex(darkerR)}${toHex(darkerG)}${toHex(darkerB)}`;
  };

  // Simple hash function to consistently assign colors based on user ID
  const getUserColor = (userId) => {
    if (!userId) return pastelColors[0];
    
    let hash = 0;
    for (let i = 0; i < userId.length; i++) {
      const char = userId.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    return pastelColors[Math.abs(hash) % pastelColors.length];
  };

  // Memoized profile stack component for header with internal hover state management
  const HeaderProfileStack = memo(({ activeUsers, maxVisible = 4 }) => {
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
    const profileMenuTimeoutRef = useRef(null);
    const profileStackRef = useRef(null);

    if (!activeUsers || activeUsers.length === 0) return null;

    // Filter out current user if present
    const filteredUsers = activeUsers.filter(activeUser => activeUser.userId !== user?.id);
    const displayUsers = filteredUsers.slice(0, maxVisible);
    const remainingCount = Math.max(0, filteredUsers.length - maxVisible);

    const handleProfileMenuEnter = () => {
      // Clear any existing timeout
      if (profileMenuTimeoutRef.current) {
        clearTimeout(profileMenuTimeoutRef.current);
        profileMenuTimeoutRef.current = null;
      }
      setIsProfileMenuOpen(true);
    };

    const handleProfileMenuLeave = () => {
      // Add a small delay before closing to allow mouse to move to menu
      profileMenuTimeoutRef.current = setTimeout(() => {
        setIsProfileMenuOpen(false);
      }, 150);
    };

    const renderUserAvatar = (activeUser, index) => {
      const initials = activeUser.userName
        .split(' ')
        .slice(0, 2)
        .map(name => name.charAt(0))
        .join('')
        .toUpperCase();

      // Get user color - use the color from the activeUser data or generate one
      const userColor = { hex: activeUser.userColor || getUserColor(activeUser.userId).hex };
      const hasProfileImage = activeUser.profileImage?.url;

      return (
        <div
          key={activeUser.userId}
          className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium overflow-hidden relative border border-white dark:border-[#0A0A0A]"
          style={{ zIndex: maxVisible - index }}
        >
          {hasProfileImage ? (
            // Profile picture with colored outline
            <div className="w-full h-full rounded-full overflow-hidden relative">
              {/* Colored border background */}
              <div 
                className="absolute inset-0 rounded-full"
                style={{ backgroundColor: userColor.hex }}
              />
              {/* Profile image with slight inset to show the colored border */}
              <div className="absolute inset-[3px] rounded-full overflow-hidden bg-white dark:bg-[#111111]">
                <img 
                  src={activeUser.profileImage.url} 
                  alt={activeUser.userName}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          ) : (
            // Monogram with colored background
            <div 
              className="w-full h-full flex items-center justify-center rounded-full"
              style={{ 
                backgroundColor: userColor.hex,
                color: getDarkerTextColor(userColor.hex)
              }}
            >
              {initials}
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="relative group">
        <div 
          ref={profileStackRef}
          className="flex items-center justify-center mt-1"
          onMouseEnter={handleProfileMenuEnter}
          onMouseLeave={handleProfileMenuLeave}
        >
          <div className="flex items-center -space-x-1">
            {displayUsers.map((user, index) => renderUserAvatar(user, index))}
            {remainingCount > 0 && (
              <div className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-300 border border-white dark:border-[#0A0A0A]">
                +{remainingCount}
              </div>
            )}
          </div>
        </div>

        {/* Profile stack hover menu - positioned relative to this container */}
        {isProfileMenuOpen && (
          <div 
            className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 z-[100]"
            onMouseEnter={() => {
              // Clear timeout when mouse enters menu
              if (profileMenuTimeoutRef.current) {
                clearTimeout(profileMenuTimeoutRef.current);
                profileMenuTimeoutRef.current = null;
              }
            }}
            onMouseLeave={handleProfileMenuLeave}
          >
            {/* Pointer tip */}
            <div className="absolute left-1/2 top-[-5px] w-2.5 h-2.5 bg-[#EDEDED] rounded-tl-[4px] dark:bg-[#161616] border-l border-t border-[#C3C3C3] dark:border-[#444444] transform -translate-x-1/2 rotate-45"></div>
            
            {/* Menu content */}
            <div className="bg-[#EDEDED] dark:bg-[#161616] rounded-2xl shadow-lg px-2 py-2 w-auto border border-[#C3C3C3] dark:border-[#444444]">
              {/* Header */}
              <div className="px-3 py-1 text-xs text-gray-500 dark:text-gray-400 font-medium flex items-center gap-2">
                Who's Here 
                <span className="bg-[#C3C3C3] dark:bg-[#444444] text-[#181818] dark:text-[#EDEDED] w-5 h-5 rounded-full text-[10px] font-medium flex items-center justify-center leading-none">
                  {filteredUsers.length}
                </span>
              </div>
              
              {/* Active users list */}
              <div className="max-h-48 overflow-y-auto scrollbar-hide">
                <div className="space-y-1">
                  {filteredUsers.map((activeUser) => {
                    // Get user initials
                    const getUserInitials = (activeUser) => {
                      if (activeUser.userName) {
                        const nameParts = activeUser.userName.trim().split(' ');
                        if (nameParts.length >= 2) {
                          return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase();
                        } else {
                          return nameParts[0].charAt(0).toUpperCase();
                        }
                      }
                      return 'U';
                    };

                    const userColor = { hex: activeUser.userColor || getUserColor(activeUser.userId).hex };
                    const initials = getUserInitials(activeUser);
                    const hasProfileImage = activeUser.profileImage?.url;

                    return (
                      <div key={activeUser.userId} className="flex items-center px-3 py-2 rounded-lg hover:bg-[#f5f5f5] dark:hover:bg-[#3a3a3a] transition-colors">
                        {/* User avatar */}
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium overflow-hidden relative mr-3 flex-shrink-0">
                          {hasProfileImage ? (
                            // Profile picture with colored outline
                            <div className="w-full h-full rounded-full overflow-hidden relative">
                              <div 
                                className="absolute inset-0 rounded-full"
                                style={{ backgroundColor: userColor.hex }}
                              />
                              <div className="absolute inset-[2px] rounded-full overflow-hidden bg-white dark:bg-[#161616]">
                                <img 
                                  src={activeUser.profileImage.url} 
                                  alt={activeUser.userName}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            </div>
                          ) : (
                            // Monogram with colored background
                            <div 
                              className={`w-full h-full flex items-center justify-center rounded-full`}
                              style={{ 
                                backgroundColor: userColor.hex,
                                color: getDarkerTextColor(userColor.hex)
                              }}
                            >
                              {initials}
                            </div>
                          )}
                        </div>
                        
                        {/* User name */}
                        <span className="text-[13px] text-[#181818] dark:text-gray-200 truncate font-loading" style={{ fontFamily: 'var(--chat-font-family)' }}>
                          {activeUser.userName}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  });

  return (
    <div className={`sticky z-[60] ${className}`}>
      <header className="flex w-full shrink-0 flex-col items-stretch bg-white dark:bg-[#111111] transition-all">
        <div className="flex w-full items-center py-3 px-4 max-[1200px]:py-2.5 max-[1200px]:px-3 max-[900px]:py-2 max-[900px]:px-2.5 [@media(max-height:800px)]:py-2.5 [@media(max-height:800px)]:px-3 [@media(max-height:700px)]:py-2 [@media(max-height:700px)]:px-2.5">
        <div className={`w-full flex items-center gap-4 relative transition-[padding] duration-300 ${isSidebarCollapsed ? 'pl-10' : 'pl-0'}`}>
          <div className="flex items-center gap-4">
            <button 
              onClick={onMenuClick}
              className="md:hidden p-2 text-gray-500 hover:text-[#181818] transition-colors hover:bg-[#ededed] rounded-[12px]"
              title="Open menu"
            >
              <Bars3Icon className="w-6 h-6" />
            </button>
            {showModelSelector && !isNotificationsOpen && (
              <HeaderModelSelector
                selectedModel={selectedModel}
                onModelSelect={onModelSelect}
                disabled={false}
              />
            )}
            {isProjectChat && (
              <div className="relative group">
                <button 
                  onClick={handleReturnToProject}
                  className="text-gray-500 dark:text-gray-400 hover:text-[#181818] dark:hover:text-gray-300 transition-colors rounded-[12px]"
                >
                  <ArrowLeftIcon className="w-6 h-6" />
                </button>
                
                {/* Tooltip */}
                <div className="absolute top-[calc(100%+8px)] left-1/2 -translate-x-1/2 opacity-0 hidden md:block group-hover:opacity-100 transition-all duration-200 pointer-events-none transform group-hover:translate-y-1 z-[100]">
                  <div className="px-3 py-1.5 bg-[#EDEDED] dark:bg-[#161616] rounded-xl border border-[#dbdbdb] dark:border-[#2a2a2a] text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap relative shadow-lg">
                    Return to project
                    <div className="absolute top-[-5px] left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-[#EDEDED] dark:bg-[#161616] rounded-tl-[4px] border-t border-l border-[#dbdbdb] dark:border-[#2a2a2a] transform rotate-45"></div>
                  </div>
                </div>
              </div>
            )}
            {/* Profile stack for project chats */}
            {isProjectChat && activeUsers && activeUsers.length > 0 && (
              <HeaderProfileStack activeUsers={activeUsers} maxVisible={4} />
            )}
          </div>
          <div className="flex-1" />
          {isSearchOpen && (
            <div className="absolute left-1/2 -translate-x-1/2 w-[400px]">
              <div className="relative">
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search in conversation..."
                  value={searchQuery}
                  onChange={handleSearchInput}
                  className={`w-full px-4 py-2 text-sm border border-gray-200 dark:border-[#444444] max-[1200px]:px-3 max-[1200px]:py-1.5 max-[1200px]:text-[13px] max-[900px]:px-2.5 max-[900px]:py-1 max-[900px]:text-[12px] [@media(max-height:800px)]:px-3 [@media(max-height:800px)]:py-1.5 [@media(max-height:800px)]:text-[13px] [@media(max-height:700px)]:px-2.5 [@media(max-height:700px)]:py-1 [@media(max-height:700px)]:text-[12px] 
                    ${searchResults.length > 0 ? 'border-yellow-400 dark:border-yellow-600' : ''} 
                    bg-[#ededed] dark:bg-[#282828] rounded-[12px] focus:outline-none focus:border-gray-400 
                    dark:focus:border-gray-400 transition-colors pr-28`}
                  autoFocus
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  {searchResults.length > 0 && (
                    <>
                      <span className="text-xs text-gray-500 dark:text-gray-400 mr-1 max-[1200px]:text-[11px] max-[900px]:text-[10px] [@media(max-height:800px)]:text-[11px] [@media(max-height:700px)]:text-[10px]">
                        {currentResultIndex + 1}/{searchResults.length}
                      </span>
                      <button 
                        onClick={navigateToPrevResult}
                        className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                        title="Previous match (Shift+Enter)"
                      >
                        <ChevronUpIcon className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={navigateToNextResult}
                        className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                        title="Next match (Enter)"
                      >
                        <ChevronDownIcon className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  <button 
                    onClick={clearSearch}
                    className="p-1 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                    title="Close search (Esc)"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="ml-auto flex items-center gap-1">
            {isNotificationsOpen && onCloseNotifications ? (
              <button
                onClick={onCloseNotifications}
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-[#181818] dark:hover:text-gray-300 transition-colors hover:bg-[#ededed] dark:hover:bg-[#282828] rounded-[12px]"
                title="Close notifications"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            ) : onNotificationsClick && (
              <button
                onClick={onNotificationsClick}
                className="relative p-2 text-gray-500 dark:text-gray-400 hover:text-[#181818] dark:hover:text-gray-300 transition-colors hover:bg-[#ededed] dark:hover:bg-[#282828] rounded-[12px]"
                title="Notifications"
              >
                <BellIcon className="w-5 h-5" />
                {notificationCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-medium rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
                    {notificationCount > 9 ? '9+' : notificationCount}
                  </span>
                )}
              </button>
            )}
            {hasBotResponses && !isNotificationsOpen && (
              <div className="flex gap-2">
                {!isSettingsOrProfile && (
                  <div className="relative">
                    <button 
                      ref={actionsMenuRef}
                      onMouseEnter={handleActionsMenuEnter}
                      onMouseLeave={handleActionsMenuLeave}
                      className="p-2 text-gray-500 dark:text-gray-400 hover:text-[#181818] dark:hover:text-gray-300 transition-colors hover:bg-[#ededed] dark:hover:bg-[#282828] rounded-[12px]"
                      title="More actions"
                    >
                      <HiDotsVertical className="w-5 h-5" />
                    </button>

                    {/* Actions menu - positioned relative to this container */}
                    <div 
                      className={`absolute top-full -right-2 mt-2 z-[100] transition-all duration-200 ease-out ${
                        isActionsMenuOpen 
                          ? 'opacity-100 transform translate-y-0 scale-100' 
                          : 'opacity-0 transform -translate-y-2 scale-95 pointer-events-none'
                      }`}
                      onMouseEnter={() => {
                        // Clear timeout when mouse enters menu
                        if (actionsMenuTimeoutRef.current) {
                          clearTimeout(actionsMenuTimeoutRef.current);
                          actionsMenuTimeoutRef.current = null;
                        }
                      }}
                      onMouseLeave={handleActionsMenuLeave}
                    >
                        {/* Pointer tip */}
                        <div className="absolute right-4 top-[-5px] w-2.5 h-2.5 bg-[#EDEDED] rounded-tl-[4px] dark:bg-[#161616] border-l border-t border-[#C3C3C3] dark:border-[#2A2A2A] transform -translate-x-1/2 rotate-45"></div>
                        
                        {/* Menu content */}
                        <div className="bg-[#EDEDED] dark:bg-[#161616] rounded-2xl shadow-lg px-2 py-2 w-auto border border-[#C3C3C3] dark:border-[#2A2A2A] min-w-[170px]">
                          {/* Menu items */}
                          <div className="space-y-1">
                            <button
                              onClick={handleFindInChat}
                              className="flex items-center w-full px-3 py-2 rounded-lg hover:bg-[#f5f5f5] dark:hover:bg-[#3a3a3a] transition-colors text-left"
                            >
                              <RxMagnifyingGlass className="w-4 h-4 mr-3 text-gray-500 dark:text-gray-400" />
                              <span className="text-[13px] text-[#181818] dark:text-gray-200 font-loading" style={{ fontFamily: 'var(--chat-font-family)' }}>
                                Find in Chat
                              </span>
                            </button>
                            <button
                              onClick={handleDownloadFromMenu}
                              disabled={isDownloading}
                              className={`flex items-center w-full px-3 py-2 rounded-lg hover:bg-[#f5f5f5] dark:hover:bg-[#3a3a3a] transition-colors text-left ${
                                isDownloading ? 'cursor-not-allowed opacity-80' : ''
                              }`}
                            >
                              <div className="relative w-4 h-4 mr-3">
                                <RxDownload className={`w-4 h-4 text-gray-500 dark:text-gray-400 ${isDownloading ? 'opacity-0' : ''}`} />
                                {isDownloading && (
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <svg className="w-4 h-4 -rotate-90 text-gray-500 dark:text-gray-400" viewBox="0 0 24 24">
                                      <circle 
                                        className="opacity-25" 
                                        cx="12" 
                                        cy="12" 
                                        r="10" 
                                        stroke="currentColor" 
                                        strokeWidth="3" 
                                        fill="none"
                                      />
                                      <circle 
                                        className="origin-center [transform-box:fill-box]"
                                        cx="12" 
                                        cy="12" 
                                        r="10" 
                                        stroke="currentColor" 
                                        strokeWidth="3" 
                                        fill="none" 
                                        strokeDasharray={`${downloadProgress * 0.628} 62.8`}
                                        strokeDashoffset="0"
                                        strokeLinecap="round"
                                      />
                                    </svg>
                                  </div>
                                )}
                              </div>
                              <span className="text-[13px] text-[#181818] dark:text-gray-200 font-loading" style={{ fontFamily: 'var(--chat-font-family)' }}>
                                {isDownloading ? 'Downloading...' : 'Download PDF'}
                              </span>
                            </button>
                          </div>
                        </div>
                      </div>
                  </div>
                )}
              </div>
            )}
            {/* Project Settings Button - only show on project dashboard */}
            {isProjectDashboard && onProjectSettings && (
              <button
                onClick={onProjectSettings}
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-[#181818] dark:hover:text-gray-300 transition-colors hover:bg-[#ededed] dark:hover:bg-[#282828] rounded-[12px] ml-2"
                title="Project Settings"
              >
                <HiDotsVertical className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
        </div>
      </header>
    </div>
  );
}